import { useEffect, useMemo, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { ICONS } from "../constants/icons";
import { FaEdit, FaTrash } from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiCopy, FiShare2 } from "react-icons/fi";
import { ROUTES } from "../App";
import { database, functions } from "../constants/firebase";
import { ref, onValue, remove, get } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import DeleteModal from "../components/DeleteModal";
import MobileHeader from "../components/MobileHeader";
import { isHostUser } from "../utils/roleUtils";
import SellerOnboarding from "../components/SellerOnboarding";
import { useLocation } from "react-router-dom";
import e from "cors";
import { copyToClipboard, deleteBunnyCollections, formatPrice } from "../utils/utils";

const MyProducts = () => {
    const navigate = useMoveTo();
    const location = useLocation();
    const { currentUser } = useUser();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("products");
    const [contentView, setContentView] = useState("all");
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, link: "" });
    const [loadingImage, setLoadingImage] = useState(true);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [sellerStatus, setSellerStatus] = useState(null);
    const [salesData, setSalesData] = useState({ totalRevenue: 0, totalSales: 0 });
    const [payouts, setPayouts] = useState([]);


    const [exchangeRates, setExchangeRates] = useState({ USD_TO_ZAR: 18.5, ZAR_TO_USD: 0.054 }); // Default rates
    const [userCountry, setUserCountry] = useState(""); // Will store user's country


    // ✅ Fetch exchange rates from an API (using free API)
    useEffect(() => {
        const fetchExchangeRates = async () => {
            try {
                // You can use a free exchange rate API
                const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
                const data = await response.json();
                if (data.rates && data.rates.ZAR) {
                    setExchangeRates({
                        USD_TO_ZAR: data.rates.ZAR,
                        ZAR_TO_USD: 1 / data.rates.ZAR
                    });
                }
            } catch (error) {
                console.error("Failed to fetch exchange rates, using defaults:", error);
                // Keep default rates if API fails
            }
        };

        fetchExchangeRates();
    }, []);

    // ✅ Determine user's country
    useEffect(() => {
        const determineUserCountry = () => {
            // Method 1: From user profile if available
            if (currentUser?.country) {
                setUserCountry(currentUser.country);
                return currentUser.country;
            }
        };

        determineUserCountry();
    }, [currentUser]);


    const handleShare = async (item) => {
        const shareLink = `${window.location.origin}/#${ROUTES.PREVIEW_PRODUCT_DETAILS.replace(':productId', item.id)}`;
        
        if (navigator.share) {
            try {
                await navigator.share({
                    title: item.title,
                    text: `Check out my product: ${item.title}`,
                    url: shareLink,
                });
            } catch (error) {
                console.error("Error sharing:", error);
                copyToClipboard(shareLink);
                window.showToast("Link copied to clipboard!", "success");
            }
        } else {
            copyToClipboard(shareLink);
            window.showToast("Link copied to clipboard!", "success");
        }
    };

    useEffect(() => {
        const closeMenu = () => setOpenMenuId(null);
        window.addEventListener("click", closeMenu);
        return () => window.removeEventListener("click", closeMenu);
    }, []);

    const getConvertedPrice = (product) => {
        if (!product?.price) return { price: 0, currency: "" };

        const price = parseFloat(product.price);
        const productCurrency = (product.currency || "").trim().toUpperCase();

        let calculatedPrice = price;
        let calculatedCurrency = productCurrency;

        if (userCountry === "South Africa") {
            if (productCurrency === "USD") {
                calculatedPrice = price * exchangeRates.USD_TO_ZAR;
                calculatedCurrency = "ZAR";
            }
        } else {
            if (productCurrency === "ZAR") {
                calculatedPrice = price * exchangeRates.ZAR_TO_USD;
                calculatedCurrency = "USD";
            }
        }

        return {
            price: calculatedPrice,
            currency: calculatedCurrency
        };
    };


    useEffect(() => {
        const closeMenu = () => setContextMenu({ ...contextMenu, visible: false });
        window.addEventListener("click", closeMenu);
        return () => window.removeEventListener("click", closeMenu);
    }, [contextMenu]);

    // Check for onboarding completion redirect
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        const onboardingStatus = params.get('onboarding');

        const view = params.get('view');

        if (tab === 'payouts') {
            setActiveTab('payouts');
        } else if (tab === 'products') {
            setActiveTab('products');
        }

        if (view === 'digital' || view === 'listings') {
            setContentView(view);
        } else {
            setContentView('all');
        }

        if (onboardingStatus === 'complete') {
            window.showToast("Seller account setup complete! You can now receive payouts.", "success");
            checkSellerStatus();
            // Clean URL
            window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash.split('?')[0]}`);
        } else if (onboardingStatus === 'refresh') {
            window.showToast("Please complete your seller account setup.", "info");
            setShowOnboarding(true);
            // Clean URL
            window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash.split('?')[0]}`);
        }
    }, [location.search]);

    const filteredListings = useMemo(() => {
        if (contentView === "all") return listings;

        if (contentView === "digital") {
            return listings.filter((item) => (item?.type || "").toLowerCase() === "course");
        }

        return listings.filter((item) => (item?.type || "").toLowerCase() !== "course");
    }, [listings, contentView]);

    useEffect(() => {
        if (!currentUser) return;

        const listingsRef = ref(database, "Listings");
        const unsubscribe = onValue(listingsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const userListings = Object.values(data).filter(
                    (item) => item.userId === currentUser.uid
                );
                // ✅ Sort by createdAt (descending)
                const sortedListings = userListings.sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                );

                setListings(sortedListings);
            } else {
                setListings([]);
            }
            setLoading(false); // ✅ stop loader once data is fetched
        });

        return () => unsubscribe();
    }, [currentUser]);

    // Check seller status for Host users
    useEffect(() => {
        if (currentUser && isHostUser(currentUser)) {
            checkSellerStatus();
            loadSalesData();
        }
    }, [currentUser]);

    // Real-time listener for seller status
    useEffect(() => {
        if (!currentUser?.uid || !isHostUser(currentUser)) return;

        const userRef = ref(database, `Users/${currentUser.uid}`);
        const unsubscribe = onValue(userRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                if (data.payoutMethod === "stripe") {
                    setSellerStatus({
                        hasAccount: !!data.stripeConnectedAccountId,
                        isComplete: data.sellerOnboardingStatus === "complete",
                        canReceivePayments: data.sellerCanReceivePayments || false,
                        method: "stripe",
                    });
                } else if (data.payoutMethod === "paystack") {
                    const hasPaystack =
                        !!data.paystackSubaccountCode &&
                        data.sellerOnboardingStatus === "complete" &&
                        data.sellerCanReceivePayments;

                    setSellerStatus({
                        hasAccount: !!data.paystackSubaccountCode,
                        isComplete: hasPaystack,
                        canReceivePayments: hasPaystack,
                        method: "paystack",
                    });

                }
            }
        });

        return () => unsubscribe();
    }, [currentUser?.uid]);

    const checkSellerStatus = async () => {
        if (!currentUser?.uid) return;

        try {
            const userRef = ref(database, `Users/${currentUser.uid}`);
            const snapshot = await new Promise((resolve) => {
                onValue(userRef, resolve, { onlyOnce: true });
            });

            if (!snapshot.exists()) return;

            const userData = snapshot.val();

            // 🟡 Check which payout method user has
            if (userData.payoutMethod === "stripe") {
                // ✅ Use Firebase Function to verify Stripe account
                const checkStatus = httpsCallable(functions, "checkConnectAccountStatus");
                const result = await checkStatus({ userId: currentUser.uid });

                setSellerStatus(result.data);
            } else if (userData.payoutMethod === "paystack") {
                // ✅ Use realtime data to determine Paystack setup
                const hasPaystack =
                    !!userData.paystackSubaccountCode &&
                    userData.sellerOnboardingStatus === "complete" &&
                    userData.sellerCanReceivePayments;

                setSellerStatus({
                    hasAccount: !!userData.paystackSubaccountCode,
                    isComplete: hasPaystack,
                    canReceivePayments: hasPaystack,
                    method: "paystack",
                });

                console.log("Paystack seller status:", hasPaystack);
                console.log("Paystack Subaccount Code:", userData.paystackSubaccountCode);
                console.log("Onboarding Status:", userData.sellerOnboardingStatus);
                console.log("Can Receive Payments:", userData.sellerCanReceivePayments);
            } else {
                // ✅ Payout settings and status
                setSellerStatus({
                    hasAccount: false,
                    isComplete: false,
                    canReceivePayments: false,
                });
            }
        } catch (error) {
            console.error("Error checking seller status:", error);
        }
    };


    const loadSalesData = () => {
        if (!currentUser?.uid) return;

        // 1️⃣ TOTALS (sales summary)
        const salesRef = ref(database, `Users/${currentUser.uid}/sales`);
        onValue(salesRef, (snapshot) => {
            let totalRevenue = 0;
            let totalSales = 0;

            if (snapshot.exists()) {
                Object.values(snapshot.val()).forEach((sale) => {
                    totalRevenue += sale.revenue || 0;
                    totalSales += sale.count || 0;
                });
            }

            setSalesData({ totalRevenue, totalSales });
        });

        // 2️⃣ SALES HISTORY (table)
        const historyRef = ref(database, `Users/${currentUser.uid}/salesHistory`);

        onValue(historyRef, async (snapshot) => {
            if (!snapshot.exists()) {
                // setPayouts(generateDummyPayouts());
                setPayouts([]);
                return;
            }

            const historyData = snapshot.val();
            const historyArray = await Promise.all(
                Object.values(historyData).map(async (sale) => {
                    const userSnap = await get(ref(database, `Users/${sale.buyerId}`));
                    const buyerData = userSnap.exists() ? userSnap.val() : {};

                    return {
                        ...sale,
                        buyerName: buyerData.name || buyerData.displayName || "",
                        buyerEmail: buyerData.email || "",

                    };
                })
            );

            const sorted = historyArray.sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );

            setPayouts(sorted);
        });


    };

    const handleOpenDashboard = async () => {
        if (!currentUser?.uid) return;

        try {
            const getDashboardLink = httpsCallable(functions, "getConnectDashboardLink");
            const result = await getDashboardLink({ userId: currentUser.uid });

            if (result.data.url) {
                window.open(result.data.url, "_blank");
            }
        } catch (error) {
            console.error("Error opening dashboard:", error);
            window.showToast("Failed to open dashboard. Please try again.", "error");
        }
    };

    const handleDelete = async () => {
        if (!selectedId) return;
        try {
            setIsDeleting(true);

            const listingSnap = await get(ref(database, `Listings/${selectedId}`));
            if (!listingSnap.exists()) throw new Error("Listing not found");
            if (listingSnap.val().type === "course") {
                const courseSnap = await get(ref(database, `Listings/${selectedId}/chapters`));
                const chapters = courseSnap.val();

                for (const chapterId in chapters) {

                    // 1️⃣ Load all lessons inside this chapter
                    const lessonsSnap = await get(
                        ref(database, `Listings/${selectedId}/chapters/${chapterId}/lessons`)
                    );

                    if (lessonsSnap.exists()) {
                        const lessons = lessonsSnap.val();

                        for (const lessonId in lessons) {
                            // 2️⃣ Delete Bunny collection for each lesson
                            await deleteBunnyCollections(`lessons_${lessonId}`);
                        }
                    }
                }
            };
            await remove(ref(database, `Listings/${selectedId}`));
            window.showToast("Listing deleted successfully", "success");
        } catch (err) {
            console.error("Delete failed:", err);
            window.showToast("Failed to delete listing", "error");
        } finally {
            setIsDeleting(false);
            setIsDeleteOpen(false);
            setSelectedId(null);
        }
    }

    const pageTitle =
        contentView === "digital"
            ? "My Digital Products"
            : contentView === "listings"
                ? "My Listings"
                : "My Products";

    return (
        <div className="h-screen flex flex-col bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
            {/* Mobile Header */}
            <MobileHeader title={pageTitle} isBackBtn={true} />

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                <Sidebar />

                {/* Main Content */}
                <main className="flex-1 overflow-y-auto pt-5 pb-24 md:pb-20 px-6">
                    {/* Top Header */}
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">{pageTitle}</h2>
                        <div />
                    </div>

                    {/* Seller Status Banner for Host Users */}
                    {isHostUser(currentUser) && (
                        <div className="md:mt-8 mt-2 mb-4">
                            {sellerStatus?.isComplete && sellerStatus?.canReceivePayments ? (
                                // <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-2 border-green-300 dark:border-green-700 rounded-xl p-4">
                                //     <div className="flex items-start justify-between gap-4">
                                //         <div className="flex items-start gap-3 flex-1">
                                //             <div className="flex-shrink-0 mt-1">
                                //                 <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                //                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                //                 </svg>
                                //             </div>
                                //             <div className="flex-1">
                                //                 <h3 className="font-semibold text-green-800 dark:text-green-200">
                                //                     ✓ Seller Account Active
                                //                 </h3>
                                //                 <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                                //                     Your payout account is fully set up. You'll receive payments directly to your bank account.
                                //                 </p>
                                //                 {(salesData.totalSales > 0 || salesData.totalRevenue > 0) && (
                                //                     <div className="flex gap-4 mt-2 text-sm">
                                //                         <span className="text-green-700 dark:text-green-300">
                                //                             <strong>Total Sales:</strong> {salesData.totalSales}
                                //                         </span>
                                //                         <span className="text-green-700 dark:text-green-300">
                                //                             <strong>Total Revenue:</strong> {currentUser.country === 'South Africa' ? `R${salesData.totalRevenue.toFixed(2)}` : `$${salesData.totalRevenue.toFixed(2)}`}
                                //                         </span>
                                //                     </div>
                                //                 )}
                                //             </div>
                                //         </div>
                                //         {
                                //             /* Dashboard Button */
                                //             sellerStatus?.method !== "paystack" && (
                                //                 <button
                                //                     onClick={handleOpenDashboard}
                                //                     className="flex-shrink-0 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2"
                                //                 >
                                //                     <span>View Dashboard</span>
                                //                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                //                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                //                     </svg>
                                //                 </button>
                                //             )
                                //         }

                                //     </div>
                                // </div>
                                <>
                                </>
                            ) : (
                                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-xl p-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-3 flex-1">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-yellow-800 dark:text-yellow-200">
                                                    ⚠ Setup Required: Connect Your Bank Account
                                                </h3>
                                                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                                    To sell products and receive payments, you need to complete your seller account setup with Stripe. This connects your bank account for automatic payouts.
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setShowOnboarding(true)}
                                            className="flex-shrink-0 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
                                        >
                                            {sellerStatus?.hasAccount ? "Continue Setup" : "Start Setup"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Action Buttons */}
                    {isHostUser(currentUser) ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mt-4 mb-8 px-2 md:px-0">
                            <button
                                className="bg-[#327691] text-white font-semibold rounded-lg px-4 py-2 text-base md:px-8 md:py-3 md:text-lg focus:outline-none transition hover:bg-[#22516a]"
                                onClick={() => navigate(ROUTES.ADD_PRODUCT)}
                            >
                                + Add Product
                            </button>

                            <button
                                className="bg-[#327691] text-white font-semibold rounded-lg px-4 py-2 text-base md:px-8 md:py-3 md:text-lg focus:outline-none transition hover:bg-[#22516a]"
                                onClick={() => navigate(ROUTES.ADD_COURSE)}
                            >
                                + Add Course
                            </button>

                            <button
                                className="bg-[#327691] text-white font-semibold rounded-lg px-4 py-2 text-base md:px-8 md:py-3 md:text-lg transition hover:bg-[#22516a]"
                                onClick={() =>
                                    setActiveTab((prev) => (prev === "products" ? "payouts" : "products"))
                                }
                            >
                                {activeTab === "products" ? "💰 Payouts" : "My Products"}
                            </button>
                            <button
                                className="bg-[#327691] text-white font-semibold rounded-lg px-4 py-2 text-base md:px-8 md:py-3 md:text-lg focus:outline-none transition hover:bg-[#22516a]"
                                onClick={() => setShowOnboarding(true)}
                            >
                                Bank details
                            </button>

                        </div>
                    ) : (
                        <div className="bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg p-4 md:mt-8 mt-2 mb-8">
                            <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
                                Host Membership Required
                            </h3>
                            <p className="text-sm text-yellow-700 dark:text-yellow-400 mb-3">
                                Only Host members can sell products in the store. Upgrade to a Host membership to start selling.
                            </p>
                            <button
                                className="bg-[#327691] text-white font-semibold rounded-lg px-6 py-2 text-base focus:outline-none transition hover:bg-[#22516a]"
                                onClick={() => navigate(ROUTES.SUBSCRIPTION_PLAN)}
                            >
                                Upgrade to Host
                            </button>
                        </div>
                    )}


                    {/* Product List */}
                    {activeTab === "products" ? (
                        <div className="space-y-4">
                            {loading ? (
                                <div className="space-y-4 mt-6">
                                    {[...Array(5)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="animate-pulse flex items-center justify-between border-t border-gray-200 dark:border-[#D9D9D9]/20 px-4 py-4"
                                        >
                                            <div className="flex items-center gap-4 w-full md:max-w-[40%]">
                                                <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                                                <div className="flex-1 space-y-2">
                                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end gap-2 min-w-[100px]">
                                                <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                                                <div className="flex gap-2">
                                                    <div className="h-3 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
                                                    <div className="h-3 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : filteredListings.length > 0 ? (
                                filteredListings.map((item) => (
                                    <div
                                        key={item.id}
                                        className={`relative flex items-center justify-between border-t text-black 
                   dark:border-[#D9D9D9]/20 border-gray-200 dark:text-white px-4 py-4 
                   hover:bg-gray-100 dark:hover:bg-[#2A2A2A] hover:rounded-lg transition-colors duration-200 ${item.type === "course" ? "cursor-pointer" : "cursor-default"}`}
                                        onClick={() => { item.type === "course" && navigate(`${ROUTES.MANAGE_CHAPTER}/${item.id}`) }}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            setContextMenu({
                                                visible: true,
                                                x: e.pageX,
                                                y: e.pageY,
                                                link: `${window.location.origin}/#${ROUTES.PRODUCT_DETAILS}/${item.id}`
                                            });
                                        }}
                                    >
                                        <div className="flex items-center gap-4 md:max-w-[40%] max-w-full">
                                            {/* Image with per-item loading state */}
                                            <div className="relative w-16 h-16 flex items-center justify-center flex-shrink-0">
                                                {!item.imageLoaded && (
                                                    <div className="absolute inset-0 bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse rounded-lg" />
                                                )}
                                                <img
                                                    src={item?.coverImageUrl || IMAGES.courses}
                                                    alt="Product"
                                                    className={`w-16 h-16 rounded-lg object-cover transition-opacity duration-300 ${item.imageLoaded ? "opacity-100" : "opacity-0"
                                                        }`}
                                                    onLoad={() => {
                                                        item.imageLoaded = true;
                                                        setListings((prev) => [...prev]);
                                                    }}
                                                    onError={() => {
                                                        item.imageLoaded = true;
                                                        setListings((prev) => [...prev]);
                                                    }}
                                                />
                                            </div>

                                            <div>
                                                <div className="font-bold md:text-lg text-sm line-clamp-1">{item.title}</div>
                                                <div className="md:text-sm text-xs text-gray-700 dark:text-white line-clamp-3 min-w-full">
                                                    {item.explainer || "No description provided"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-4 text-sm">
                                            {item.price && (
                                                <div className="font-bold text-xl text-[#327691]">
                                                    {(() => {
                                                        const converted = getConvertedPrice(item);
                                                        return formatPrice(converted.price, converted.currency, item.currency);
                                                    })()}
                                                </div>
                                            )}
                                            <div className="flex items-center md:gap-4 gap-1 text-sm">
                                                <button
                                                    className="flex items-center gap-1 text-green-500 hover:cursor-pointer bg-gray-200 dark:bg-gray-500/30 px-2 py-1 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(
                                                            item.type === "course" ? ROUTES.ADD_COURSE : ROUTES.ADD_PRODUCT,
                                                            { state: { course: item.type === "course" ? item : null, product: item.type === "product" ? item : null } }
                                                        );
                                                    }}
                                                >
                                                    <FaEdit /> Edit
                                                </button>
                                                <span className="text-gray-400">|</span>
                                                <button
                                                    className="flex items-center gap-1 text-red-500 hover:cursor-pointer bg-gray-200 dark:bg-gray-500/30 px-2 py-1 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedId(item.id);
                                                        setIsDeleteOpen(true);
                                                    }}
                                                >
                                                    <FaTrash /> Delete
                                                </button>
                                                <span className="text-gray-400">|</span>
                                                <div className="relative">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenMenuId(openMenuId === item.id ? null : item.id);
                                                        }}
                                                        className="p-1.5 rounded-md hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors bg-gray-200 dark:bg-gray-500/30"
                                                    >
                                                        <BsThreeDotsVertical className="text-gray-700 dark:text-white" />
                                                    </button>

                                                    {openMenuId === item.id && (
                                                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1F1F1F] shadow-xl rounded-xl border border-gray-200 dark:border-gray-700 py-2 z-50 animate-in fade-in zoom-in duration-200">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const shareLink = `${window.location.origin}/#${ROUTES.PREVIEW_PRODUCT_DETAILS.replace(':productId', item.id)}`;
                                                                    copyToClipboard(shareLink);
                                                                    window.showToast("Link copied to clipboard!", "success");
                                                                    setOpenMenuId(null);
                                                                }}
                                                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
                                                            >
                                                                <FiCopy className="w-4 h-4" />
                                                                Copy Link
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleShare(item);
                                                                    setOpenMenuId(null);
                                                                }}
                                                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
                                                            >
                                                                <FiShare2 className="w-4 h-4" />
                                                                Share Externally
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))

                            ) : (
                                <p className="text-center text-gray-500 dark:text-gray-400 mt-10">
                                    {contentView === "digital"
                                        ? "No digital products found."
                                        : contentView === "listings"
                                            ? "No listings found."
                                            : "No products or courses found."}
                                </p>
                            )}
                        </div>
                    ) : (
                        /* 💰 PAYOUTS VIEW */
                        <div className="mt-6 space-y-6">
                            {/* Summary Cards */}
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="bg-green-100 dark:bg-green-900/20 p-4 rounded-xl">
                                    <h3 className="font-semibold text-green-800 dark:text-green-300">
                                        Total Revenue
                                    </h3>
                                    <p className="text-2xl font-bold">
                                        {currentUser.country === "South Africa"
                                            ? `R${salesData.totalRevenue.toFixed(2)}`
                                            : `$${salesData.totalRevenue.toFixed(2)}`}
                                    </p>
                                </div>

                                <div className="bg-blue-100 dark:bg-blue-900/20 p-4 rounded-xl">
                                    <h3 className="font-semibold text-blue-800 dark:text-blue-300">
                                        Total Sales
                                    </h3>
                                    <p className="text-2xl font-bold">{salesData.totalSales}</p>
                                </div>
                            </div>

                            {/* Sales Table */}
                            <div className="bg-white dark:bg-[#2A2A2A] rounded-xl border dark:border-gray-700">
                                <div className="p-4 border-b dark:border-gray-700">
                                    <h3 className="font-semibold text-lg">Sales History</h3>
                                </div>

                                {payouts.length === 0 ? (
                                    <p className="p-4 text-gray-500">No sales yet.</p>
                                ) : (
                                    <div className="divide-y dark:divide-gray-700">
                                        {payouts.map((sale, index) => (
                                            <div
                                                key={index}
                                                className="p-4 flex justify-between items-start"
                                            >
                                                <div className="space-y-1">
                                                    <p className="font-semibold">{sale.productTitle}</p>

                                                    <p className="text-sm text-black/70 dark:text-[#FFFFFFCC]">
                                                        Buyer: {sale.buyerName || "N/A"}
                                                    </p>

                                                    <p className="text-sm text-black/70 dark:text-[#FFFFFFCC]">
                                                        Email: {sale.buyerEmail}
                                                    </p>

                                                    <p className="text-xs text-black/70 dark:text-[#FFFFFFCC]">
                                                        {new Date(sale.createdAt).toLocaleDateString()}
                                                    </p>
                                                </div>

                                                <div className="text-right space-y-1">
                                                    {/* SALE AMOUNT */}
                                                    <p className="font-bold text-green-600 text-lg">
                                                        {currentUser.country === "South Africa"
                                                            ? `R${sale.saleAmount?.toFixed(2)}`
                                                            : `$${sale.saleAmount?.toFixed(2)}`}
                                                    </p>

                                                    {/* SETTLED AMOUNT */}
                                                    <p className="text-sm text-black/70 dark:text-[#FFFFFFCC]">
                                                        Settled:
                                                        <span className="font-semibold ml-1">
                                                            {currentUser.country === "South Africa"
                                                                ? `R${sale.settledAmount?.toFixed(2)}`
                                                                : `$${sale.settledAmount?.toFixed(2)}`}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {contextMenu.visible && (
                        <div
                            className="absolute z-50 bg-white dark:bg-[#2A2A2A] border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-2"
                            style={{ top: contextMenu.y, left: contextMenu.x }}
                        >
                            <button
                                className="block w-full text-left px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-[#3A3A3A] rounded"
                                onClick={() => {
                                    navigator.clipboard.writeText(contextMenu.link);
                                    window.showToast("Link copied to clipboard!", "success");
                                    setContextMenu({ ...contextMenu, visible: false });
                                }}
                            >
                                Copy Link
                            </button>
                        </div>
                    )}


                    {/* ✅ Delete Confirmation Modal */}
                    <DeleteModal
                        isOpen={isDeleteOpen}
                        onClose={() => setIsDeleteOpen(false)}
                        onConfirm={handleDelete}
                        isDeleting={isDeleting}
                    />

                    {/* Seller Onboarding Modal */}
                    <SellerOnboarding
                        isOpen={showOnboarding}
                        onClose={() => setShowOnboarding(false)}
                    />
                </main>
            </div>
        </div>
    );
};

export default MyProducts;
